import type { DesignerDocument } from '../types.js';

/**
 * Options for CSS export.
 */
export interface ExportCSSOptions {
  /** CSS selector to scope the custom properties under. Default: ":root" */
  readonly selector?: string;
  /** Whether to include a comment header with metadata. Default: true */
  readonly header?: boolean;
}

/**
 * Generate a CSS string containing only the changed (overridden) tokens.
 *
 * Output format:
 * ```css
 * :root {
 *   --bq-p500: 255, 0, 0;
 *   --bq-br3: 12px;
 * }
 * ```
 *
 * Returns an empty string if no overrides exist.
 */
export function exportCSS(
  doc: DesignerDocument,
  options?: ExportCSSOptions,
): string {
  if (doc.overrides.size === 0) {
    return '';
  }

  const selector = options?.selector ?? ':root';

  // BT-11: Validate selector to prevent CSS injection via the public API.
  // Allow typical selectors: :root, .class, #id, [attr], element, combinators.
  // Reject anything containing { } ; < > which could break CSS structure.
  if (/[{}<>;]/.test(selector)) {
    throw new Error('Invalid CSS selector: contains forbidden characters');
  }

  const includeHeader = options?.header ?? true;

  // Sort overrides by cssVar for deterministic output
  const sorted = Array.from(doc.overrides.values()).sort((a, b) =>
    a.path.cssVar.localeCompare(b.path.cssVar),
  );

  const lines: string[] = [];

  if (includeHeader) {
    lines.push(`/* CRD Designer — ${sorted.length} token override${sorted.length === 1 ? '' : 's'} */`);
  }

  lines.push(`${selector} {`);
  for (const override of sorted) {
    // BT-04: Defense-in-depth — strip chars that could break CSS structure
    const safeValue = override.value.replace(/[;{}]/g, '');
    lines.push(`  ${override.path.cssVar}: ${safeValue};`);
  }
  lines.push('}');
  lines.push('');

  return lines.join('\n');
}
